'use client';
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Activity, Navigation, Trash2, Check, Edit, Users, X, EyeOff, Eye } from 'lucide-react';

interface VerticalLineMenuProps {
  open: boolean;
  onClose: () => void;
  onEdit?: (newLabel: string) => void;
  lineData?: {
    id: string;
    label: string;
    source?: 'system' | 'user';
    username?: string;
    timestamp?: number;
    deletable?: boolean;
    dimmed?: boolean;
  };
  onSetAsBenchmark: () => void;
  onSetAsHarmonics: () => void;
  onSetAsSideBands: () => void;
  onRemove: () => void;
  onDim?: () => void; // Toggle dim state for non-deletable lines
  position?: { x: number; y: number };
  enableCompareTable?: boolean;
  enableHarmonics?: boolean;
  enableSideBands?: boolean;
  isBenchmark?: boolean; // Whether this line is currently set as benchmark
  canRemove?: boolean; // Whether the Remove option should be enabled (default: true for user lines)
}

/**
 * Menu that appears when clicking on a vertical line (marker/compare line)
 * This menu is ONLY for non-harmonic lines (compare mode markers)
 * Provides "Set as Benchmark", "Add Harmonics", "Add Side Bands", and "Remove" options
 */
export const VerticalLineMenu: React.FC<VerticalLineMenuProps> = ({
  open,
  onClose,
  onEdit,
  lineData,
  onSetAsBenchmark,
  onSetAsHarmonics,
  onSetAsSideBands,
  onRemove,
  onDim,
  position,
  enableCompareTable = false,
  enableHarmonics = false,
  enableSideBands = false,
  isBenchmark = false,
  canRemove = true, // Default to true for backward compatibility
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // State for inline editing
  const [isEditing, setIsEditing] = useState(false);
  const [editingLabel, setEditingLabel] = useState('');

  // State for adjusted position (collision detection)
  const [adjustedPosition, setAdjustedPosition] = useState<{ x: number; y: number } | null>(null);

  // Calculate adjusted position to prevent viewport overflow
  useEffect(() => {
    if (!open || !position) {
      setAdjustedPosition(null);
      return;
    }

    // Wait for menu to render to get its dimensions
    requestAnimationFrame(() => {
      if (!menuRef.current) return;

      const menu = menuRef.current;
      const rect = menu.getBoundingClientRect();
      const padding = 8; // Padding from viewport edges

      let adjustedX = position.x;
      let adjustedY = position.y;

      // Check right overflow
      if (position.x + rect.width > window.innerWidth - padding) {
        adjustedX = position.x - rect.width;
      }

      // Check bottom overflow
      if (position.y + rect.height > window.innerHeight - padding) {
        adjustedY = position.y - rect.height;
      }

      // Ensure menu doesn't go off the left or top
      adjustedX = Math.max(padding, adjustedX);
      adjustedY = Math.max(padding, adjustedY);

      setAdjustedPosition({ x: adjustedX, y: adjustedY });
    });
  }, [open, position]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Handle starting edit mode
  const handleStartEdit = () => {
    if (lineData?.label) {
      setEditingLabel(lineData.label);
      setIsEditing(true);
    }
  };

  // Handle saving edit
  const handleSaveEdit = () => {
    if (onEdit && editingLabel.trim()) {
      onEdit(editingLabel.trim());
    }
    setIsEditing(false);
    onClose();
  };

  // Handle canceling edit
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingLabel('');
  };

  // Handle key presses in input
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  // Click outside handler to close menu
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Add delay to prevent immediate closing from the same click that opened it
    setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onClose]);

  if (!open) return null;

  // Use adjusted position if calculated, otherwise use initial position
  const finalPosition = adjustedPosition || position;

  return (
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        left: finalPosition?.x || 0,
        top: finalPosition?.y || 0,
        zIndex: 9999,
        // Hide initially until position is adjusted to prevent flash
        visibility: adjustedPosition ? 'visible' : 'hidden',
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-2 min-w-[180px]">
        {/* Header with line info */}
        {lineData && (
          <div className="px-2 py-1 mb-2 border-b border-gray-200 dark:border-gray-600">
            {isEditing ? (
              <div className="flex items-center gap-1">
                <Input
                  ref={inputRef}
                  value={editingLabel}
                  onChange={(e) => setEditingLabel(e.target.value)}
                  onKeyDown={handleKeyPress}
                  className="text-sm font-medium h-7 px-2"
                  placeholder="Enter label"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={handleSaveEdit}
                >
                  <Check className="w-3 h-3 text-green-600" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={handleCancelEdit}
                >
                  <X className="w-3 h-3 text-red-600" />
                </Button>
              </div>
            ) : (
              <div className="text-sm font-medium">{lineData.label}</div>
            )}
            {lineData.username && (
              <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                <Users className="w-3 h-3" />
                {lineData.username}
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col gap-0.5">
          {/* Edit option */}
          {onEdit && !isEditing && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start gap-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={handleStartEdit}
              >
                <Edit className="w-4 h-4" />
                Edit Label
              </Button>
              <div className="border-t border-gray-200 dark:border-gray-600 my-1" />
            </>
          )}

          {/* Always show "Set as Benchmark" option for markers */}
          <Button
            variant="ghost"
            size="sm"
            className={`w-full justify-start gap-2 ${
              isBenchmark
                ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900/30'
                : 'hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            onClick={() => {
              onSetAsBenchmark();
              onClose();
            }}
          >
            <Check className="w-4 h-4" />
            {isBenchmark ? 'Benchmark (Active)' : 'Set as Benchmark'}
          </Button>
          {/* HIDDEN: Create Harmonics and Create Side Bands options */}
          {/* {(enableHarmonics || enableSideBands) && (
            <div className="border-t border-gray-200 dark:border-gray-600 my-1" />
          )} */}

          {/* Show "Create Harmonics" only if harmonics is enabled */}
          {/* {enableHarmonics && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2 hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => {
                onSetAsHarmonics();
                onClose();
              }}
            >
              <Activity className="w-4 h-4" />
              Create Harmonics
            </Button>
          )} */}

          {/* Show "Create Side Bands" only if side bands is enabled */}
          {/* {enableSideBands && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2 hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => {
                onSetAsSideBands();
                onClose();
              }}
            >
              <Navigation className="w-4 h-4" />
              Create Side Bands
            </Button>
          )} */}

          {/* Dim option - always available */}
          {onDim && (
            <>
              <div className="border-t border-gray-200 dark:border-gray-600 my-1" />
              <Button
                variant="ghost"
                size="sm"
                className={`w-full justify-start gap-2 hover:bg-gray-100 dark:hover:bg-gray-700 ${
                  lineData?.dimmed ? 'text-primary-600 dark:text-primary-400' : ''
                }`}
                onClick={() => {
                  onDim();
                  onClose();
                }}
              >
                {lineData?.dimmed ? (
                  <>
                    <Eye className="w-4 h-4" />
                    Undim
                  </>
                ) : (
                  <>
                    <EyeOff className="w-4 h-4" />
                    Dim
                  </>
                )}
              </Button>
            </>
          )}

          {/* Remove option - only when canRemove is true */}
          {canRemove && (
            <>
              {!onDim && <div className="border-t border-gray-200 dark:border-gray-600 my-1" />}
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start gap-2 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400"
                onClick={() => {
                  onRemove();
                  onClose();
                }}
              >
                <Trash2 className="w-4 h-4" />
                Remove
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
